import { gql } from '@apollo/client';
import { useTimeSettings } from '@mnd-frontend/contexts';
import { shortDateTimeStringWithTz } from '@mnd-frontend/dates';
import { MonitorSourceVerification, type Sentiment } from '@mnd-frontend/graphql-schema';
import {
  Avatar,
  Box,
  Button,
  GREY_400,
  GREY_800,
  GREY_1000,
  Icons,
  SIZING_2,
  SPACING_1,
  SPACING_2,
  SPACING_4,
  Text,
  withLineClamp,
} from '@mnd-frontend/ui';
import { localizeValue } from '@mnd-frontend/utils';
import styled from 'styled-components';
import { useHitsByIdsQuery, useSentimentHitsQuery } from '../../../../../generated/hooks';
import { useReportStaticData, useReportTranslation } from '../../../hooks';
import { AudioPlayer } from '../AudioPlayer';
import { SentimentDropdown } from '../SentimentDropdown';
import { SkeletonHit, SkeletonWrapper } from '../Skeleton';

gql`
  query HitsByIds($reportId: ID!, $from: DateTime!, $to: DateTime!, $searchProfileIds: [ID!]) {
    report2(id: $reportId) {
      id
      ... on BrandReport {
        hits: hits2(
          timeRange: { from: $from, to: $to }
          orderBy: REACH
          searchProfileIds: $searchProfileIds
        ) {
          id
          sentiment
          sourceName
          publishedAt
          title
          hitLine
          link
          thumbnailUrl
          reach
          sourceVerification
          audioUrl
          audioHitlines {
            id
            startPos
            endPos
            hitline
          }
        }
      }
    }
  }

  query SentimentHits(
    $reportId: ID!
    $from: DateTime!
    $to: DateTime!
    $sentiment: Sentiment
    $searchProfileIds: [ID!]
  ) {
    report2(id: $reportId) {
      id
      ... on BrandReport {
        sentimentAnalysisHits(
          timeRange: { from: $from, to: $to }
          sentiment: $sentiment
          searchProfileIds: $searchProfileIds
        ) {
          id
          sentiment
          sentimentExplanation
          sourceName
          publishedAt
          title
          hitLine
          link
          thumbnailUrl
          reach
          sourceVerification
          audioUrl
          audioHitlines {
            id
            startPos
            endPos
            hitline
          }
        }
      }
    }
  }
`;

export interface HitsModalDetails {
  startAt: string;
  endAt: string;
  label: string;
  filter?: Sentiment;
}

export const HitsModal = ({
  onLinkClick,
  onClose,
  details,
  searchProfileIds,
}: {
  onLinkClick?: () => void;
  onClose: () => void;
  details: HitsModalDetails;
  searchProfileIds?: string[] | null;
}) => {
  const { t } = useReportTranslation();
  const { id: reportId } = useReportStaticData();
  const { startAt, endAt } = details;
  const { data, loading } = useHitsByIdsQuery({
    variables: {
      reportId,
      from: startAt,
      to: endAt,
      searchProfileIds,
    },
  });
  const hits = data?.report2?.__typename === 'BrandReport' ? data?.report2.hits : [];

  return (
    <Inner
      header={t('reports_sentiment_articles_in_time_frame', {
        timeLabel: details.label,
      })}
      hits={hits}
      loading={loading}
      onClose={onClose}
      onLinkClick={onLinkClick}
    />
  );
};

export const HitsModalSentiment = ({
  onLinkClick,
  onClose,
  details,
  searchProfileIds,
}: {
  onLinkClick?: () => void;
  onClose: () => void;
  details: HitsModalDetails;
  searchProfileIds?: string[] | null;
}) => {
  const { t } = useReportTranslation();
  const { id: reportId } = useReportStaticData();
  const { startAt, endAt } = details;
  const { data, loading } = useSentimentHitsQuery({
    variables: {
      reportId,
      from: startAt,
      to: endAt,
      sentiment: details.filter,
      searchProfileIds,
    },
  });
  const hits =
    data?.report2?.__typename === 'BrandReport' ? data?.report2.sentimentAnalysisHits : [];

  return (
    <Inner
      header={t('reports_sentiment_articles_in_time_frame', {
        timeLabel: details.label,
      })}
      hits={hits}
      loading={loading}
      onClose={onClose}
      onLinkClick={onLinkClick}
      showSentiment={true}
    />
  );
};

const List = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${SPACING_4};
`;
const Item = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${SPACING_1};
  &:not(:last-child)::after {
    content: '';
    display: block;
    width: 100%;
    border-bottom: 1px solid ${GREY_400};
    margin-top: ${SPACING_2};
  }
`;
const ReachContainer = styled.div`
  color: ${GREY_800};
  align-items: center;
  display: flex;
  gap: ${SPACING_1};
`;
const A = styled.a`
  display: block;
  &:hover {
    text-decoration: underline;
  }
`;
const Header = styled.div`
  display: flex;
  flex-direction: row;
  gap: ${SPACING_2};
`;
const HeaderRightTop = styled.div`
  display: flex;
  flex-direction: row;
  gap: ${SPACING_1};
`;
const HeaderRight = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0;
`;
const Footer = styled.div`
  display: flex;
  flex-direction: row;
  gap: ${SPACING_2};
  justify-content: space-between;
`;
const LineClamp = styled.span<{ lines: number }>`
  ${({ lines }) => withLineClamp(lines)}
`;

const HitText = styled.p`
  color: ${GREY_1000};
  font-size: ${SIZING_2};
  margin:0;
  font-weight: 400;
  line-height: 1.5rem;
`;

const Inner = ({
  loading,
  onClose,
  hits,
  onLinkClick,
  header,
  showSentiment,
}: {
  loading: boolean;
  header: string;
  onClose: () => void;
  hits: {
    id: string;
    sourceName?: string | null;
    publishedAt: string;
    title?: string | null;
    thumbnailUrl?: string | null;
    link?: string | null;
    hitLine?: string | null;
    reach: number;
    sentiment?: Sentiment | null;
    sourceVerification: MonitorSourceVerification;
    audioUrl?: string | null;
    audioHitlines?: { startPos: number; endPos: number; hitline: string }[];
  }[];
  onLinkClick?: () => void;
  showSentiment?: boolean;
}) => {
  const {
    material: { timezone, locale },
  } = useTimeSettings();
  const { t } = useReportTranslation();

  return (
    <Box
      openedAsModal
      size="lg"
      onClose={onClose}
      loading={loading}
      description={header}
      footerContent={
        <>
          {onLinkClick ? (
            <Button onClick={onLinkClick} configuration="text" type="button">
              {t('preview_media_feed-see-more')}
            </Button>
          ) : null}
          <Button onClick={onClose} type="button">
            {t('common_close')}
          </Button>
        </>
      }
    >
      <List>
        {loading &&
          Array.from({ length: 2 }).map((_, i) => (
            <Item key={i}>
              <SkeletonWrapper>
                <SkeletonHit />
              </SkeletonWrapper>
            </Item>
          ))}
        {!loading && hits.length === 0 && <Item>{t('reports_no_mentions_found')}</Item>}
        {!loading &&
          hits.map(hit => {
            return (
              <Item key={hit.id}>
                <Header>
                  <Avatar type="thumbnail" image={hit.thumbnailUrl} />
                  <HeaderRight>
                    <HeaderRightTop>
                      <Text as="h3" variant="labelSmall">
                        {hit.sourceName}
                      </Text>
                      {hit.publishedAt && (
                        <div>
                          <Text as="span" variant="labelSmall">
                            {t('analyze_materials_state_published')}:{' '}
                          </Text>
                          <time dateTime={hit.publishedAt}>
                            <Text as="span" variant="labelSmall">
                              {shortDateTimeStringWithTz(hit.publishedAt, timezone, locale)}
                            </Text>
                          </time>
                        </div>
                      )}
                    </HeaderRightTop>
                    <div>
                      {hit.title && (
                        <Text as="h3" variant="headlineSmall">
                          <LineClamp lines={1}>{hit.title}</LineClamp>
                        </Text>
                      )}
                    </div>
                  </HeaderRight>
                </Header>
                {hit.audioUrl && !!hit.audioHitlines?.length && (
                  <AudioPlayer audioUrl={hit.audioUrl} hitlines={hit.audioHitlines} />
                )}
                {!(hit.audioUrl && hit.audioHitlines?.length) &&
                  (hit.link ? (
                    <A href={hit.link} target="_blank" rel="noopener">
                      <HitText dangerouslySetInnerHTML={{ __html: hit.hitLine || '' }} />
                    </A>
                  ) : (
                    <HitText dangerouslySetInnerHTML={{ __html: hit.hitLine || '' }} />
                  ))}
                <Footer>
                  <ReachContainer>
                    {hit.sourceVerification !== MonitorSourceVerification.Rejected && (
                      <>
                        <Icons.Reach />
                        <Text as="span" variant="labelSmall">
                          {hit.reach > 0 ? localizeValue(hit.reach, { abbrev: true }) : 'N/A'}
                        </Text>
                      </>
                    )}
                  </ReachContainer>
                  {showSentiment && (
                    <SentimentDropdown skipLabel={true} value={hit.sentiment ?? undefined} />
                  )}
                </Footer>
              </Item>
            );
          })}
      </List>
    </Box>
  );
};
